import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Trash2, Edit, Save, X, 
  Shield, ShieldCheck, User, Mail, 
  CheckCircle, XCircle, Search, 
  UserPlus, Key, Power, Eye, EyeOff, AlertTriangle,
  MailCheck, MailX, Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementModule = ({ showToast }) => {
  const { user: currentUser, isSuperAdmin, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    newPassword: '',
    currentPassword: '',
    role: 'user'
  });
  const [errors, setErrors] = useState({});

  const roleLabels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    user: 'User'
  };

  const roleColors = {
    super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
    admin: 'bg-blue-100 text-blue-700 border-blue-200',
    user: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  const roleIcons = {
    super_admin: <ShieldCheck size={14} />,
    admin: <Shield size={14} />,
    user: <User size={14} />
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(data);
    } catch (error) {
      showToast('Gagal memuat data pengguna', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    }
    return true;
  });

  // Check if current user can edit target user
  const canEditUser = (targetUser) => {
    if (isSuperAdmin()) return true; // Super Admin can edit anyone
    if (targetUser.id === currentUser?.id) return true; // Can edit self
    return false; // Admin/User cannot edit others
  };

  // Check if current user can manage (toggle active) target user
  const canManageUser = (targetUser) => {
    if (!isAdmin()) return false; // Only admin+ can manage
    if (targetUser.id === currentUser?.id) return false; // Cannot manage self
    if (!isSuperAdmin() && targetUser.role === 'super_admin') return false; // Admin cannot manage super_admin
    return isSuperAdmin(); // Only super admin can toggle active
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nama wajib diisi';
    if (!formData.email.trim()) newErrors.email = 'Email wajib diisi';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Format email tidak valid';
    if (!editingUser && formData.password.length < 6) newErrors.password = 'Password minimal 6 karakter';
    if (editingUser && formData.newPassword && formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password minimal 6 karakter';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingUser) {
        const updateData = { name: formData.name, email: formData.email };
        
        // Only Super Admin can change role
        if (isSuperAdmin() && formData.role && editingUser.id !== currentUser?.id) {
          updateData.role = formData.role;
        }
        
        await axios.put(`${API}/users/${editingUser.id}`, updateData, { withCredentials: true });
        
        // Handle password change
        if (formData.newPassword) {
          if (editingUser.id === currentUser?.id) {
            // Self - use change-password endpoint with current password
            if (!formData.currentPassword) {
              showToast('Masukkan password saat ini untuk mengubah password', 'error');
              return;
            }
            await axios.post(`${API}/auth/change-password`, 
              { current_password: formData.currentPassword, new_password: formData.newPassword }, 
              { withCredentials: true }
            );
            showToast('Password berhasil diubah', 'success');
          } else if (isSuperAdmin()) {
            // Super Admin changing other's password
            await axios.patch(`${API}/users/${editingUser.id}/reset-password`, 
              { new_password: formData.newPassword }, 
              { withCredentials: true }
            );
          }
        }
        
        showToast('Pengguna berhasil diperbarui', 'success');
      } else {
        await axios.post(`${API}/users`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role
        }, { withCredentials: true });
        showToast('Pengguna berhasil ditambahkan', 'success');
      }
      resetForm();
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal menyimpan pengguna', 'error');
    }
  };

  const handleDelete = async () => {
    if (!editingUser) return;
    
    try {
      await axios.delete(`${API}/users/${editingUser.id}`, { withCredentials: true });
      showToast('Pengguna berhasil dihapus', 'success');
      resetForm();
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal menghapus pengguna', 'error');
    }
    setShowDeleteConfirm(false);
  };

  const handleToggleActive = async (userId) => {
    try {
      const { data } = await axios.patch(`${API}/users/${userId}/toggle-active`, {}, { withCredentials: true });
      showToast(data.message, 'success');
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal mengubah status pengguna', 'error');
    }
  };

  const handleResendVerification = async (userId) => {
    try {
      const { data } = await axios.post(`${API}/users/${userId}/resend-verification`, {}, { withCredentials: true });
      showToast(data.message, data.email_sent ? 'success' : 'info');
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal mengirim ulang verifikasi', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', newPassword: '', currentPassword: '', role: 'user' });
    setEditingUser(null);
    setShowForm(false);
    setErrors({});
    setShowPassword(false);
    setShowDeleteConfirm(false);
  };

  const handleEditClick = (user) => {
    // Check permission
    if (!canEditUser(user)) {
      showToast('Anda tidak memiliki izin untuk mengedit pengguna ini', 'error');
      return;
    }
    
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      newPassword: '',
      currentPassword: '',
      role: user.role || 'user'
    });
    setEditingUser(user);
    setShowForm(true);
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">Akses Ditolak</h3>
          <p className="text-slate-500 mt-2">Anda tidak memiliki izin untuk mengakses halaman ini.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">Manajemen Pengguna</h2>
          <p className="text-muted-foreground mt-1">Kelola pengguna dan hak akses sistem</p>
        </div>
        {isSuperAdmin() && (
          <button 
            onClick={() => showForm ? resetForm() : setShowForm(true)} 
            className={showForm ? "btn-secondary" : "btn-primary"}
            data-testid="add-user-btn"
          >
            {showForm ? <><X size={18}/> Batal</> : <><UserPlus size={18}/> Tambah Pengguna</>}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row gap-4 justify-between">
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Cari pengguna..."
              className="input-field pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-user-input"
            />
          </div>
          <select 
            className="input-field w-auto"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            data-testid="role-filter-select"
          >
            <option value="all">Semua Role</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card p-6 space-y-6"
            data-testid="user-form"
          >
            <div className="flex items-center justify-between border-b border-border pb-4">
              <h3 className="font-heading font-semibold text-lg">
                {editingUser ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}
              </h3>
              {editingUser && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${roleColors[editingUser.role]}`}>
                  {roleIcons[editingUser.role]}
                  {roleLabels[editingUser.role]}
                </span>
              )}
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div className="flex flex-col space-y-2">
                  <label className="label">Nama Lengkap <span className="text-destructive">*</span></label>
                  <input 
                    className={`input-field ${errors.name ? 'border-destructive' : ''}`}
                    value={formData.name} 
                    onChange={e => { setFormData({...formData, name: e.target.value}); setErrors({...errors, name: ''}); }}
                    placeholder="Nama pengguna" 
                    data-testid="user-name-input"
                  />
                  {errors.name && <span className="text-xs text-destructive">{errors.name}</span>}
                </div>
                
                <div className="flex flex-col space-y-2">
                  <label className="label">Email <span className="text-destructive">*</span></label>
                  <input 
                    type="email"
                    className={`input-field ${errors.email ? 'border-destructive' : ''}`}
                    value={formData.email} 
                    onChange={e => { setFormData({...formData, email: e.target.value}); setErrors({...errors, email: ''}); }}
                    placeholder="email@example.com" 
                    data-testid="user-email-input"
                  />
                  {errors.email && <span className="text-xs text-destructive">{errors.email}</span>}
                </div>

                {/* Password for new user */}
                {!editingUser && (
                  <>
                    <div className="flex flex-col space-y-2">
                      <label className="label">Password <span className="text-destructive">*</span></label>
                      <div className="relative">
                        <input 
                          type={showPassword ? 'text' : 'password'}
                          className={`input-field pr-10 ${errors.password ? 'border-destructive' : ''}`}
                          value={formData.password} 
                          onChange={e => { setFormData({...formData, password: e.target.value}); setErrors({...errors, password: ''}); }}
                          placeholder="Minimal 6 karakter" 
                          data-testid="user-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                        </button>
                      </div>
                      {errors.password && <span className="text-xs text-destructive">{errors.password}</span>}
                    </div>
                    
                    <div className="flex flex-col space-y-2">
                      <label className="label">Role <span className="text-destructive">*</span></label>
                      <select 
                        className="input-field bg-white"
                        value={formData.role}
                        onChange={e => setFormData({...formData, role: e.target.value})}
                        data-testid="new-user-role-select"
                      >
                        <option value="user">User - Akses baca saja</option>
                        <option value="admin">Admin - Kelola data</option>
                        <option value="super_admin">Super Admin - Akses penuh</option>
                      </select>
                      <span className="text-xs text-muted-foreground">Pilih role untuk pengguna baru</span>
                    </div>
                  </>
                )}
              </div>

              {/* Super Admin Only Section */}
              {editingUser && isSuperAdmin() && editingUser.id !== currentUser?.id && (
                <div className="border-t border-border pt-6">
                  <h4 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
                    <ShieldCheck size={16} className="text-purple-600"/>
                    Aksi Super Admin
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {/* Role Change */}
                    <div className="flex flex-col space-y-2">
                      <label className="label">Ubah Role</label>
                      <select 
                        className="input-field bg-white"
                        value={formData.role}
                        onChange={e => setFormData({...formData, role: e.target.value})}
                        data-testid="user-role-select"
                      >
                        <option value="user">User - Akses baca saja</option>
                        <option value="admin">Admin - Kelola data</option>
                        <option value="super_admin">Super Admin - Akses penuh</option>
                      </select>
                      <span className="text-xs text-muted-foreground">Role menentukan hak akses pengguna dalam sistem</span>
                    </div>
                    
                    {/* Password Reset */}
                    <div className="flex flex-col space-y-2">
                      <label className="label">Reset Password</label>
                      <div className="relative">
                        <input 
                          type={showPassword ? 'text' : 'password'}
                          className={`input-field pr-10 ${errors.newPassword ? 'border-destructive' : ''}`}
                          value={formData.newPassword} 
                          onChange={e => { setFormData({...formData, newPassword: e.target.value}); setErrors({...errors, newPassword: ''}); }}
                          placeholder="Password baru (kosongkan jika tidak diubah)" 
                          data-testid="user-new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                        </button>
                      </div>
                      {errors.newPassword && <span className="text-xs text-destructive">{errors.newPassword}</span>}
                      <span className="text-xs text-muted-foreground">Kosongkan jika tidak ingin mengubah password</span>
                    </div>
                  </div>

                  {/* Delete User Section */}
                  <div className="mt-6 p-4 bg-rose-50 border border-rose-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h5 className="font-semibold text-rose-800">Zona Berbahaya</h5>
                        <p className="text-sm text-rose-600 mt-1">Menghapus pengguna bersifat permanen dan tidak dapat dibatalkan.</p>
                        
                        {!showDeleteConfirm ? (
                          <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(true)}
                            className="mt-3 px-4 py-2 bg-white border border-rose-300 text-rose-600 rounded-lg text-sm font-medium hover:bg-rose-100 transition-colors"
                            data-testid="show-delete-btn"
                          >
                            <Trash2 size={16} className="inline mr-2"/>
                            Hapus Pengguna Ini
                          </button>
                        ) : (
                          <div className="mt-3 flex items-center gap-3">
                            <span className="text-sm text-rose-700 font-medium">Yakin hapus?</span>
                            <button
                              type="button"
                              onClick={handleDelete}
                              className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700 transition-colors"
                              data-testid="confirm-delete-btn"
                            >
                              Ya, Hapus
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowDeleteConfirm(false)}
                              className="px-4 py-2 bg-white border border-slate-300 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors"
                            >
                              Batal
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Self Edit Notice */}
              {editingUser && editingUser.id === currentUser?.id && !isSuperAdmin() && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <strong>Info:</strong> Anda sedang mengedit profil Anda sendiri. Untuk mengubah password, gunakan menu Pengaturan.
                  </p>
                </div>
              )}

              {/* Super Admin Self Edit - with password change */}
              {editingUser && editingUser.id === currentUser?.id && isSuperAdmin() && (
                <div className="border-t border-border pt-6">
                  <h4 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
                    <Key size={16} className="text-teal-700"/>
                    Ubah Password
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <div className="flex flex-col space-y-2">
                      <label className="label">Password Saat Ini</label>
                      <div className="relative">
                        <input 
                          type={showPassword ? 'text' : 'password'}
                          className={`input-field pr-10 ${errors.currentPassword ? 'border-destructive' : ''}`}
                          value={formData.currentPassword || ''} 
                          onChange={e => { setFormData({...formData, currentPassword: e.target.value}); setErrors({...errors, currentPassword: ''}); }}
                          placeholder="Masukkan password saat ini" 
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                        </button>
                      </div>
                      {errors.currentPassword && <span className="text-xs text-destructive">{errors.currentPassword}</span>}
                    </div>
                    
                    <div className="flex flex-col space-y-2">
                      <label className="label">Password Baru</label>
                      <div className="relative">
                        <input 
                          type={showPassword ? 'text' : 'password'}
                          className={`input-field pr-10 ${errors.newPassword ? 'border-destructive' : ''}`}
                          value={formData.newPassword} 
                          onChange={e => { setFormData({...formData, newPassword: e.target.value}); setErrors({...errors, newPassword: ''}); }}
                          placeholder="Password baru (kosongkan jika tidak diubah)" 
                          data-testid="self-new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                        </button>
                      </div>
                      {errors.newPassword && <span className="text-xs text-destructive">{errors.newPassword}</span>}
                      <span className="text-xs text-muted-foreground">Kosongkan jika tidak ingin mengubah password</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <button type="button" onClick={resetForm} className="btn-secondary">
                  Batal
                </button>
                <button type="submit" className="btn-primary" data-testid="save-user-btn">
                  <Save size={16}/> {editingUser ? 'Perbarui' : 'Simpan'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Users Table - Desktop */}
      <div className="card overflow-hidden hidden lg:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="table-header">
              <tr>
                <th className="px-3 py-4 text-left w-12">No</th>
                <th className="px-3 py-4 text-left w-[18%]">Nama</th>
                <th className="px-3 py-4 text-left w-[22%]">Email</th>
                <th className="px-3 py-4 text-center w-[10%]">Role</th>
                <th className="px-3 py-4 text-center w-[9%]">Status</th>
                <th className="px-3 py-4 text-center w-[11%]">Verifikasi</th>
                <th className="px-3 py-4 text-left w-[10%]">Tgl Daftar</th>
                <th className="px-3 py-4 text-right w-[10%]">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="px-3 py-12 text-center text-muted-foreground">Memuat data...</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan="8" className="px-3 py-12 text-center text-muted-foreground">
                  {searchQuery ? 'Tidak ada hasil ditemukan.' : 'Belum ada pengguna.'}
                </td></tr>
              ) : filteredUsers.map((user, index) => (
                <tr key={user.id} className="table-row" data-testid={`user-row-${user.id}`}>
                  <td className="px-3 py-3 text-muted-foreground">{index + 1}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-700 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-teal-700">(Anda)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-muted-foreground truncate">{user.email}</td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${roleColors[user.role]}`}>
                      {roleIcons[user.role]}
                      {roleLabels[user.role]}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                      user.is_active !== false 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                        : 'bg-slate-100 text-slate-500 border border-slate-200'
                    }`}>
                      {user.is_active !== false ? (
                        <><CheckCircle size={11}/> Aktif</>
                      ) : (
                        <><XCircle size={11}/> Nonaktif</>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                      user.is_verified !== false 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      {user.is_verified !== false ? (
                        <><MailCheck size={11}/> Verified</>
                      ) : (
                        <><MailX size={11}/> Belum</>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('id-ID', {
                      day: 'numeric', month: 'short', year: 'numeric'
                    }) : '-'}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-0.5">
                      {/* Edit - only self or super_admin can edit all */}
                      {canEditUser(user) && (
                        <button 
                          onClick={() => handleEditClick(user)} 
                          className="p-1.5 hover:bg-slate-100 rounded-lg text-muted-foreground hover:text-primary transition-colors"
                          title="Edit"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit size={15}/>
                        </button>
                      )}
                      {/* Toggle active - only super_admin */}
                      {canManageUser(user) && (
                        <button 
                          onClick={() => handleToggleActive(user.id)} 
                          className={`p-1.5 rounded-lg transition-colors ${
                            user.is_active !== false 
                              ? 'hover:bg-amber-50 text-muted-foreground hover:text-amber-600' 
                              : 'hover:bg-emerald-50 text-muted-foreground hover:text-emerald-600'
                          }`}
                          title={user.is_active !== false ? 'Nonaktifkan' : 'Aktifkan'}
                          data-testid={`toggle-active-${user.id}`}
                        >
                          <Power size={15}/>
                        </button>
                      )}
                      {/* Resend verification - only super_admin for unverified users */}
                      {isSuperAdmin() && user.is_verified === false && user.id !== currentUser?.id && (
                        <button 
                          onClick={() => handleResendVerification(user.id)} 
                          className="p-1.5 hover:bg-teal-50 rounded-lg text-muted-foreground hover:text-teal-700 transition-colors"
                          title="Kirim Ulang Verifikasi"
                          data-testid={`resend-verify-${user.id}`}
                        >
                          <Send size={15}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Users Cards - Mobile */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <div className="card p-8 text-center text-muted-foreground">Memuat data...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="card p-8 text-center text-muted-foreground">
            {searchQuery ? 'Tidak ada hasil ditemukan.' : 'Belum ada pengguna.'}
          </div>
        ) : filteredUsers.map((user) => (
          <div key={user.id} className="card p-4" data-testid={`user-card-${user.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900 truncate">{user.name}</p>
                  {user.id === currentUser?.id && (
                    <span className="text-xs text-teal-700 font-medium">(Anda)</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${roleColors[user.role]}`}>
                    {roleIcons[user.role]}
                    {roleLabels[user.role]}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    user.is_active !== false 
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                      : 'bg-slate-100 text-slate-500 border border-slate-200'
                  }`}>
                    {user.is_active !== false ? <CheckCircle size={10}/> : <XCircle size={10}/>}
                    {user.is_active !== false ? 'Aktif' : 'Nonaktif'}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    user.is_verified !== false 
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}>
                    {user.is_verified !== false ? <MailCheck size={10}/> : <MailX size={10}/>}
                    {user.is_verified !== false ? 'Verified' : 'Unverified'}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            {(canEditUser(user) || canManageUser(user) || (isSuperAdmin() && user.is_verified === false && user.id !== currentUser?.id)) && (
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/50">
                <div className="flex-1" />
                {canEditUser(user) && (
                  <button 
                    onClick={() => handleEditClick(user)} 
                    className="p-2 hover:bg-slate-100 rounded-lg text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`edit-user-mobile-${user.id}`}
                  >
                    <Edit size={18}/>
                  </button>
                )}
                {canManageUser(user) && (
                  <button 
                    onClick={() => handleToggleActive(user.id)} 
                    className={`p-2 rounded-lg transition-colors ${
                      user.is_active !== false 
                        ? 'hover:bg-amber-50 text-muted-foreground hover:text-amber-600' 
                        : 'hover:bg-emerald-50 text-muted-foreground hover:text-emerald-600'
                    }`}
                    data-testid={`toggle-active-mobile-${user.id}`}
                  >
                    <Power size={18}/>
                  </button>
                )}
                {isSuperAdmin() && user.is_verified === false && user.id !== currentUser?.id && (
                  <button 
                    onClick={() => handleResendVerification(user.id)} 
                    className="p-2 hover:bg-teal-50 rounded-lg text-muted-foreground hover:text-teal-700 transition-colors"
                    data-testid={`resend-verify-mobile-${user.id}`}
                  >
                    <Send size={18}/>
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Role Legend - Hidden on mobile */}
      <div className="card p-4 hidden sm:block">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Keterangan Role:</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${roleColors.super_admin}`}>
              {roleIcons.super_admin} Super Admin
            </span>
            <span className="text-muted-foreground">- Akses penuh ke semua fitur</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${roleColors.admin}`}>
              {roleIcons.admin} Admin
            </span>
            <span className="text-muted-foreground">- Kelola data, tidak bisa ubah role</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${roleColors.user}`}>
              {roleIcons.user} User
            </span>
            <span className="text-muted-foreground">- Akses baca saja</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementModule;
